import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import './App.css'
import Dashboards from './pages/Dashboards'
import Assistant from './pages/Assistant'
import Main from './pages/Main'
import FloatingChat from './components/FloatingChat'
import { useMsal } from '@azure/msal-react';
import { loadChatHistory, Message, addNewMessage } from './services/chatApi';
import Admin from './pages/Admin'
import ErrorPage from './pages/ErrorPage'
import AdminConsole from './pages/AdminConsole'
import ErrorSnack from './components/ErrorSnack'
import { loadUserInfo, UserInfo, loadAllowedDomains } from './services/common'
import NavBar3 from './components/NavBar3'

const App: React.FC = () => {
    const [inputValue, setInputValue] = useState('');
    const [userInfo, setUserInfo] = React.useState<UserInfo>(null);
    const [isChatLoading, setIsChatLoading] = React.useState(false);
    const { instance } = useMsal();

    const [chatId, setChatId] = useState(() => {
        const saved = localStorage.getItem('chatId');
        return saved !== undefined && !!saved ? JSON.parse(saved) : null;
    });

    useEffect(() => {
        localStorage.setItem('chatId', JSON.stringify(chatId));
    }, [chatId]);

    const [messages, setMessages] = useState<Message[]>(() => {
        const saved = localStorage.getItem('chatMessages');
        return saved ? JSON.parse(saved) : [];
    });

    useEffect(() => {
        localStorage.setItem('chatMessages', JSON.stringify(messages));
    }, [messages]);

    const loadChatHistoryForChat = async (forceNewChat: boolean, useDetailedLog, u: UserInfo) =>
    {
        if (u?.hasAnyRole === true)
        {
            setIsChatLoading(true);
            try {
                const messages = await loadChatHistory(forceNewChat, chatId, setChatId, instance, useDetailedLog);
                setMessages(messages);
            }
            finally {
                setIsChatLoading(false);
            }
        }
    };

    const loadComponent = async () => {
        // Load domains and user info in parallel
        const [userInfo, _] = await Promise.all([
            loadUserInfo(instance),
            loadAllowedDomains() // Preload domains
        ]);
        
        setUserInfo(userInfo);
        loadChatHistoryForChat(true, false, userInfo);
    };

    useEffect(() => {
        loadComponent();
    }, []);

    const handleInputChange = (e) =>
    {
        if (e.target.value.indexOf('"') > 0) {
            setInputValue(e.target.value.replace('"', "'"));
        }
        else {
            setInputValue(e.target.value);
        }
    }

    const handleDeleteChat = async () =>
    {
        setIsChatLoading(true);
        try
        {
            setChatId(null);
            const saved = localStorage.getItem('traceVisible');
            const loadDetails = saved ? JSON.parse(saved) : false;
            await loadChatHistoryForChat(true, loadDetails, userInfo);
        }
        finally
        {
            setIsChatLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) =>
    {
        setIsChatLoading(true);
        try
        {
            const tempValue = inputValue;
            setInputValue('');
            const newMessage = await addNewMessage(chatId, setChatId, e, tempValue, setMessages, instance);
            setMessages(prev => [...prev, newMessage]);
        }
        finally {
            setIsChatLoading(false);
        }
    };

  return (
    <Router>
      <div className="app">
        <NavBar3 userInfo={userInfo}/>
        <main className="main-content">
        <Routes>
            <Route path="/" element={<Main userInfo={userInfo} />} />
            <Route path="/dashboards" element={<Dashboards />} />
            <Route path="/assistant" element={<Assistant inputValue={inputValue} deleteChatHandler={handleDeleteChat} submitHandler={handleSubmit} messages={messages} setInputHandler={handleInputChange} isLoading={isChatLoading} />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/errorPage" element={<ErrorPage />} />
            <Route path="/adminConsole" element={<AdminConsole />} />
            <Route path="*"  element={<Navigate to="/errorPage?id=404" replace={true} />} />
        </Routes>
              </main>
              {userInfo?.hasAnyRole === true ? <FloatingChat inputValue={inputValue} deleteChatHandler={handleDeleteChat} submitHandler={handleSubmit} messages={messages} setInputHandler={handleInputChange} isLoading={isChatLoading} /> : ''}
        <ErrorSnack />
      </div>
    </Router>
  )
}

export default App
